import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import QRCode from "qrcode";
import { ShieldCheck, KeyRound, ScanLine, Loader2 } from "lucide-react";

/**
 * /accept-invite?token=... — public page used by platform employees to finish
 * onboarding. Three steps:
 *   1. Lookup invite (display the employee's name + role)
 *   2. Set password (becomes their permanent login password)
 *   3. Scan TOTP QR + enter 6-digit code → account activated + session cookies issued
 */
export default function AcceptInvite() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const navigate = useNavigate();
    const [phase, setPhase] = useState("loading");  // loading | error | password | totp | done
    const [invite, setInvite] = useState(null);
    const [errMsg, setErrMsg] = useState("");
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [code, setCode] = useState("");
    const [secret, setSecret] = useState("");
    const [qrDataUrl, setQrDataUrl] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!token) { setPhase("error"); setErrMsg("Missing invite token"); return; }
            try {
                const { data } = await api.get(`/platform/accept-invite/${token}`);
                if (!cancelled) { setInvite(data); setPhase("password"); }
            } catch (e) {
                if (!cancelled) { setErrMsg(apiError(e)); setPhase("error"); }
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    const submitPassword = async (e) => {
        e.preventDefault();
        if (pw.length < 8) return toast.error("Password must be at least 8 characters");
        if (pw !== pw2) return toast.error("Passwords don't match");
        setBusy(true);
        try {
            const { data } = await api.post("/platform/accept-invite", { token, password: pw });
            setSecret(data.totp_secret_base32);
            const qr = await QRCode.toDataURL(data.totp_uri, { margin: 1, color: { dark: "#D4AF37", light: "#0A0A0A" }, width: 220 });
            setQrDataUrl(qr);
            setPhase("totp");
        } catch (err) { toast.error(apiError(err)); }
        finally { setBusy(false); }
    };

    const submitTotp = async (e) => {
        e.preventDefault();
        if (!/^\d{6}$/.test(code)) return toast.error("Enter the 6-digit code from your authenticator");
        setBusy(true);
        try {
            await api.post("/platform/accept-invite/totp/verify", { token, code });
            setPhase("done");
            toast.success("Welcome to Law OS — you're in.");
            setTimeout(() => navigate("/app", { replace: true }), 800);
        } catch (err) { toast.error(apiError(err)); }
        finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="accept-invite-page">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-primary mb-3">
                        <ShieldCheck className="w-3 h-3"/>Law OS · Secure onboarding
                    </div>
                    <h1 className="font-serif text-3xl">You&apos;re invited.</h1>
                    {invite && (
                        <p className="text-muted-foreground text-sm mt-3">
                            Joining as <span className="text-primary">{invite.role_label}</span><br/>
                            <span className="text-xs">{invite.email}</span>
                        </p>
                    )}
                </div>

                <div className="border border-border bg-card p-6">
                    {phase === "loading" && (
                        <div className="text-center text-muted-foreground py-8 flex flex-col items-center gap-3">
                            <Loader2 className="w-5 h-5 animate-spin text-primary"/>
                            <span className="text-xs">Verifying invite…</span>
                        </div>
                    )}

                    {phase === "error" && (
                        <div className="text-center space-y-4 py-4">
                            <div className="text-red-400 text-sm" data-testid="invite-error">{errMsg || "Invalid invite"}</div>
                            <div className="text-xs text-muted-foreground">Ask your super admin to send a fresh invitation.</div>
                        </div>
                    )}

                    {phase === "password" && (
                        <form onSubmit={submitPassword} className="space-y-4" data-testid="invite-password-form">
                            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary mb-1">
                                <KeyRound className="w-3 h-3"/>Step 1 of 2 · Set password
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2 block">New password</label>
                                <input type="password" required minLength={8} value={pw} onChange={e => setPw(e.target.value)}
                                    className="w-full bg-background border border-border focus:border-primary outline-none px-4 py-2.5 text-sm"
                                    data-testid="invite-password-input"/>
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2 block">Confirm password</label>
                                <input type="password" required value={pw2} onChange={e => setPw2(e.target.value)}
                                    className="w-full bg-background border border-border focus:border-primary outline-none px-4 py-2.5 text-sm"
                                    data-testid="invite-password-confirm"/>
                            </div>
                            <div className="text-[11px] text-muted-foreground">Use at least 8 characters with a mix of upper, lower, digit and symbol.</div>
                            <button type="submit" disabled={busy} data-testid="invite-password-submit"
                                className="w-full bg-primary text-primary-foreground hover:bg-[#C9A537] disabled:opacity-50 font-semibold uppercase tracking-[0.15em] text-xs py-3">
                                {busy ? "Setting…" : "Continue → Two-factor"}
                            </button>
                        </form>
                    )}

                    {phase === "totp" && (
                        <form onSubmit={submitTotp} className="space-y-4" data-testid="invite-totp-form">
                            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary mb-1">
                                <ScanLine className="w-3 h-3"/>Step 2 of 2 · Enroll authenticator
                            </div>
                            <p className="text-xs text-muted-foreground">
                                Open Google Authenticator, Authy, or 1Password on your phone, scan the QR or enter the secret manually.
                            </p>
                            <div className="flex justify-center py-2">
                                {qrDataUrl && <img src={qrDataUrl} alt="TOTP QR" className="border border-primary/40 p-2" data-testid="invite-totp-qr"/>}
                            </div>
                            <div className="bg-background border border-border p-3 text-center">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Manual entry</div>
                                <div className="font-mono text-xs text-primary break-all" data-testid="invite-totp-secret">{secret}</div>
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2 block">6-digit code</label>
                                <input inputMode="numeric" autoComplete="one-time-code" required maxLength={6} pattern="\d{6}"
                                    value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ""))}
                                    placeholder="000 000"
                                    className="w-full bg-background border border-border focus:border-primary outline-none px-4 py-3 text-center text-2xl tracking-[0.5em] font-mono"
                                    data-testid="invite-totp-code"/>
                            </div>
                            <button type="submit" disabled={busy} data-testid="invite-totp-submit"
                                className="w-full bg-primary text-primary-foreground hover:bg-[#C9A537] disabled:opacity-50 font-semibold uppercase tracking-[0.15em] text-xs py-3">
                                {busy ? "Verifying…" : "Verify & activate account"}
                            </button>
                        </form>
                    )}

                    {phase === "done" && (
                        <div className="text-center py-8 space-y-2">
                            <div className="inline-flex items-center justify-center w-12 h-12 bg-emerald-500/20 border border-emerald-500/40 mx-auto">
                                <ShieldCheck className="w-6 h-6 text-emerald-400"/>
                            </div>
                            <div className="text-sm text-emerald-300">You&apos;re in. Redirecting…</div>
                        </div>
                    )}
                </div>

                <div className="text-center text-[10px] text-muted-foreground mt-6">
                    Need help? Ask your super admin to resend the invite.
                </div>
            </div>
        </div>
    );
}
